"use client";
import { useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination} from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import { trackArrowClick } from './BannerizationAnalytics';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { componentNameConstants } from '@repo/analytics/data-layer.constants';

const BANNER_NAV_BUTTON_BASE_CLASS =
  "text-link-primary absolute top-[46%] z-10 hidden h-[48px] w-[42px] -translate-y-1/2 items-center justify-center bg-white pr-1 pl-[6px] py-2 shadow-[0_0_10px_rgba(0,0,0,0.2)] md:flex";

interface BannerCarouselProps {
  children: React.ReactNode[];
  isLoop: string;
  autoplay: string;
  autoTimer: number;
  moduleName: string;
  sectionName: string;
}

const BannerCarousel: React.FC<BannerCarouselProps> = ({ 
  children, 
  isLoop, 
  moduleName,
  sectionName,
  autoplay,
  autoTimer
}) => {
  const swiperRef = useRef<SwiperType | null>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const isAutoplayEnabled = autoplay === 'true';
  const isLoopEnabled = isLoop === 'true';

  const handlePrevClick = () => {
    trackArrowClick(componentNameConstants.BANNER_CAROUSEL, 'left', moduleName,sectionName);
    swiperRef.current?.slidePrev();
  };

  const handleNextClick = () => {
    trackArrowClick(componentNameConstants.BANNER_CAROUSEL, 'right', moduleName,sectionName);
    swiperRef.current?.slideNext();
  };

  const updateNavigationState = (swiper: SwiperType) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  return (
    <Swiper
      modules={[Autoplay, Navigation, Pagination]}
      onSwiper={(swiper) => {
        swiperRef.current = swiper;
        updateNavigationState(swiper);
      }}
      onSlideChange={(swiper) => {
        updateNavigationState(swiper);
      }}
      slidesPerView={1}
      autoplay={isAutoplayEnabled ? {
        delay: autoTimer*1000 || 4000,
        disableOnInteraction: false,
      } : false}  
      navigation={false}
      loop={isLoopEnabled || false}
      pagination={{
        clickable: true,
        el: '.banner-swiper-pagination',
      }}
      className="banner-swiper"
    >
      {children.map((child, index) => (
        <SwiperSlide key={`${moduleName}-${sectionName}-${index}`} className="cursor-pointer">
          {child}
        </SwiperSlide>
      ))}
      
      {(!isLoopEnabled && !isBeginning || isLoopEnabled) && (
        <button 
          onClick={handlePrevClick}
          className={`banner-swiper-button-prev ${BANNER_NAV_BUTTON_BASE_CLASS} left-0 rounded-r-md`}
          aria-label="Previous slide"
        >
          <i className="icomoon text-sm before:content-['\e912']"></i>
        </button>
      )}
      
      {(!isLoopEnabled && !isEnd || isLoopEnabled) && (
        <button 
          onClick={handleNextClick}
          className={`banner-swiper-button-next ${BANNER_NAV_BUTTON_BASE_CLASS} right-0 rounded-l-md`}
          aria-label="Next slide"
        >
          <i className="icomoon text-sm before:content-['\e911']"></i>
        </button>
      )}

      <div className="banner-swiper-pagination absolute bottom-4 left-0 right-0 z-10 flex justify-center"></div>
    </Swiper>
  );
};

export default BannerCarousel;